import { api } from '../api.js';
import type { ClientErrorReport, ClientErrorReportResponse } from '../../shared/api-types.js';
import { boundText, CLIENT_ERROR_REPORT_LIMITS, type ClientErrorKind } from '../../shared/client-errors.js';
import { APP_VERSION } from '../../shared/version.js';

// Why: app-baseline 05-client §5.1–§5.2. A boundary shows a reference instead of a stack; the clause wants that
// reference "carried in the report the app sends to its own telemetry", so support can open what the person quotes.
// This module is the one place the client builds and sends such a report — from a region boundary (`render`), the
// window's `error` event or an `unhandledrejection` — through the one HTTP wrapper (api.ts, spec §13). It is
// fire-and-forget: a failed report is never a second failure for the person, and nothing here can throw into the
// handler that called it. Conformity finding D2; the owner's brief of 2026-10-05, group B.

/** A quotable key to the log entry, never the stack itself (05-client §5.2): `ui-` plus eight hex digits. */
export function newReference(): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/gu, '')
    : Math.random().toString(16).slice(2);
  return `ui-${random.padEnd(8, '0').slice(0, 8)}`;
}

/** The message and stack of whatever was thrown — an Error, a string, or anything a promise rejected with. */
export function describeThrown(thrown: unknown): { message: string; stack: string | null } {
  if (thrown instanceof Error) {
    return { message: thrown.message || thrown.name || 'Error', stack: typeof thrown.stack === 'string' ? thrown.stack : null };
  }
  if (typeof thrown === 'string') return { message: thrown, stack: null };
  if (thrown === null || thrown === undefined) return { message: 'Unknown error', stack: null };
  try {
    return { message: String(thrown), stack: null };
  } catch {
    return { message: 'Unknown error', stack: null };
  }
}

export interface ClientErrorInput {
  kind: ClientErrorKind;
  /** The reference the boundary shows — minted with `newReference()` so the person and the log share it. */
  reference: string;
  thrown: unknown;
  componentStack?: string | null | undefined;
  /** The location's pathname; defaults to the window's. Never the query or the hash. */
  path?: string | null | undefined;
}

/** Builds the bounded report (pure): every field cut to the shared limits so the server never has to refuse one. */
export function buildClientErrorReport(input: ClientErrorInput, version: string = APP_VERSION): ClientErrorReport {
  const { message, stack } = describeThrown(input.thrown);
  const limits = CLIENT_ERROR_REPORT_LIMITS;
  const path = input.path === undefined ? currentPath() : input.path;
  return {
    reference: input.reference,
    kind: input.kind,
    version: boundText(version, limits.version),
    message: boundText(message, limits.message),
    stack: stack === null ? null : boundText(stack, limits.stack),
    componentStack: input.componentStack ? boundText(input.componentStack, limits.componentStack) : null,
    path: path === null ? null : boundText(path.split(/[?#]/u)[0] ?? '', limits.path),
  };
}

function currentPath(): string | null {
  return typeof window !== 'undefined' && window.location ? window.location.pathname : null;
}

/**
 * Reports one page load may send. Why a budget: a render loop or a timer that throws every tick would otherwise post
 * once per tick; ten carries the first occurrences and their references, and the console still shows every one.
 */
export const REPORTS_PER_PAGE_LOAD = 10;

export interface ClientErrorReporter {
  /** Posts the report, or drops it once the budget is spent. Never throws; resolves when the attempt is over. */
  report(input: ClientErrorInput): Promise<void>;
  /** How many reports have been posted (attempted) this page load. */
  readonly sent: number;
  /** How many were dropped for want of budget. */
  readonly dropped: number;
}

export interface ClientErrorReporterOptions {
  /** The transport; defaults to POST /api/client-errors through api.ts. Tests pass a recorder. */
  post?: (report: ClientErrorReport) => Promise<unknown>;
  budget?: number;
  version?: string;
}

const defaultPost = (report: ClientErrorReport): Promise<unknown> =>
  // Why noRedirect: a 401 here (a session that ended between the failure and the report) must never reload the page
  // from under the person who is reading the error state — the boundary is what they need to see.
  api.post<ClientErrorReportResponse>('/api/client-errors', report, { noRedirect: true });

export function createClientErrorReporter(options: ClientErrorReporterOptions = {}): ClientErrorReporter {
  const post = options.post ?? defaultPost;
  const budget = options.budget ?? REPORTS_PER_PAGE_LOAD;
  let sent = 0;
  let dropped = 0;
  return {
    get sent() {
      return sent;
    },
    get dropped() {
      return dropped;
    },
    async report(input) {
      if (sent >= budget) {
        dropped += 1;
        return;
      }
      sent += 1;
      try {
        await post(buildClientErrorReport(input, options.version));
      } catch {
        // Why swallowed: the report is a courtesy to support, not a feature for the person; its failure is not theirs.
      }
    },
  };
}

/** The application's reporter: one budget per page load, shared by every boundary and the global handlers. */
export const clientErrors: ClientErrorReporter = createClientErrorReporter();

type ErrorTarget = Pick<Window, 'addEventListener' | 'removeEventListener'>;

/**
 * Posts errors no boundary sees — an event handler, a timer, a rejected promise nobody awaited (05-client §5.1
 * "rendering and runtime errors"). Each gets its own reference, logged to the console beside the error as the
 * boundary does, so a person who opens the console still finds the key. Returns the uninstaller.
 */
export function installGlobalErrorReporting(target: ErrorTarget, reporter: ClientErrorReporter = clientErrors): () => void {
  const onError = (event: ErrorEvent): void => {
    const reference = newReference();
    const thrown: unknown = event.error ?? event.message;
    console.error(`[calis ${reference}] uncaught error:`, thrown);
    void reporter.report({ kind: 'error', reference, thrown });
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    const reference = newReference();
    console.error(`[calis ${reference}] unhandled rejection:`, event.reason);
    void reporter.report({ kind: 'unhandledrejection', reference, thrown: event.reason });
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}
